/**
 * Client-side permission helpers.
 *
 * Permissions live in the enriched JWT (`permissions`, `permission_grants`).
 * The login `user` blob in localStorage may not include them, so we always
 * fall back to decoding the JWT when needed.
 */
import { readNormalizedUserFromStorage } from './entitlements';

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

function normalizeModule(mod) {
  return String(mod ?? '*').trim().toLowerCase();
}

/** Policy microservice scopes grants to module `policy`; product entitlement is `security`. */
function moduleMatches(grantModule, requiredModule) {
  const grant = normalizeModule(grantModule);
  const required = normalizeModule(requiredModule);
  if (!required || grant === '*') return true;
  if (grant === required) return true;
  if ((grant === 'policy' && required === 'security') || (grant === 'security' && required === 'policy')) {
    return true;
  }
  return false;
}

export function getCurrentUser() {
  return readNormalizedUserFromStorage();
}

export function isPrivilegedUser(user = getCurrentUser()) {
  if (!user || typeof user !== 'object') return false;
  if (user.is_admin) return true;

  const roleName = String(user.role_name || '').toLowerCase();
  if (roleName === 'super admin' || roleName === 'administrator' || roleName === 'admin') {
    return true;
  }

  const roles = Array.isArray(user.roles) ? user.roles : [];
  return roles.some((r) => {
    const name = String(r?.role || '').toLowerCase();
    return name === 'super admin' || name === 'administrator' || name === 'admin';
  });
}

export function getPermissionGrants(user = getCurrentUser()) {
  const jwt = decodeJwtPayload(typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null);

  if (Array.isArray(jwt?.permission_grants) && jwt.permission_grants.length > 0) {
    return jwt.permission_grants;
  }

  if (Array.isArray(user?.permission_grants) && user.permission_grants.length > 0) {
    return user.permission_grants;
  }

  const names = new Set();
  (user?.permissions || []).forEach((p) => names.add(p));
  (jwt?.permissions || []).forEach((p) => names.add(p));

  return [...names].map((name) => ({ name, module: '*' }));
}

export function hasPermission(permissionName, options = {}) {
  if (!permissionName) return true;

  const user = options.user ?? getCurrentUser();
  if (isPrivilegedUser(user)) return true;

  const requiredModule = options.module ?? null;
  const perm = String(permissionName).trim();

  return getPermissionGrants(user).some((grant) => {
    if (grant?.name !== perm) return false;
    if (!requiredModule) return true;
    return moduleMatches(grant.module, requiredModule);
  });
}

export function hasAnyPermission(permissionNames, options = {}) {
  const list = Array.isArray(permissionNames) ? permissionNames : [permissionNames];
  return list.some((name) => hasPermission(name, options));
}

/** Code Security gate policy authoring (the SAST service's PUT /api/policy rule). */
export function canManageSastPolicy(user = getCurrentUser()) {
  return hasPermission('manage_policies', { module: 'sast', user });
}

/** Cloud Discovery posture checks an org adds (the monitoring service's manage_policies rule). */
export function canManageCloudPolicy(user = getCurrentUser()) {
  return hasPermission('manage_policies', { module: 'monitoring', user });
}

/** Browser Endpoint policy authoring (DLP config, tool catalog edits, push). */
export function canManageEndpointPolicy(user = getCurrentUser()) {
  return hasPermission('manage_policies', { module: 'policy', user });
}

/**
 * The login `user` blob exactly as ProtectedRoute reads it: a `{ user: {...} }`
 * wrapper is flattened with the outer keys winning.
 */
export function readRouteUser() {
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    user = {};
  }
  return user && typeof user === 'object' && user.user && typeof user.user === 'object'
    ? { ...user.user, ...user }
    : (user && typeof user === 'object' ? user : {});
}

/**
 * ProtectedRoute's `requiredRole` rule: a privileged user (admin / super admin),
 * or a user whose role_name or one of whose roles is `requiredRole`.
 */
export function hasRequiredRole(requiredRole, user = readRouteUser()) {
  if (!requiredRole) return true;
  if (!user || typeof user !== 'object') return false;
  const wanted = String(requiredRole).toLowerCase();
  const roleName = String(user.role_name || '').toLowerCase();
  const roles = Array.isArray(user.roles) ? user.roles : [];
  return isPrivilegedUser(user)
    || roleName === wanted
    || roles.some((r) => String(r?.role || '').toLowerCase() === wanted);
}

/** The user belongs to an organization (super admins carry no organization_id). */
export function hasOrganization(user = readRouteUser()) {
  if (!user || typeof user !== 'object') return false;
  const id = user.organization_id ?? user.org_id ?? user.organizationId ?? user.organization?.organization_id;
  return id !== undefined && id !== null && String(id).trim() !== '';
}

/**
 * Billing & Usage is the org's own bill: the /billing route's gate
 * (requiredRole="Administrator", routes/index.jsx) and an organization to bill.
 * A super admin with no organization passes the route's role check but has no
 * bill of their own, so the link is not offered to them.
 */
export function canViewBilling(user = readRouteUser()) {
  return hasRequiredRole('Administrator', user) && hasOrganization(user);
}
